import isEqual from 'lodash.isequal';
import { useState, useCallback, memo, useEffect, createElement } from 'react';
import { registerCoeffectHandler, coeffect, registerEffectHandler } from 'reffects';
import deepmerge from 'deepmerge';
import isPlainObject from 'lodash.isplainobject';

function getPathArray(path) {
  return Array.isArray(path) ? path : path.split('.');
}

function get(obj, path, defaultValue = null) {
  if (typeof path === 'undefined') {
    return defaultValue;
  }

  return getPathArray(path).reduce((segment, index) => {
    return segment && segment[index];
  }, obj);
}

function set(obj, path, newValue) {
  if (path.length === 0) {
    return obj;
  }
  return getPathArray(path).reduceRight(
    (value, currentPathKey, index, pathKeys) => {
      const objectSegment = get(obj, pathKeys.slice(0, index));

      if (Array.isArray(objectSegment)) {
        const aggregateArray = [...objectSegment];
        aggregateArray[currentPathKey] = value;
        return aggregateArray;
      }

      return {
        ...objectSegment,
        [currentPathKey]: value,
      };
    },
    newValue
  );
}

const getIn = (state, path, defaultValue) => {
  return get(state, path, defaultValue);
};

const setIn = (state, path, valueToSet) => {
  return set({ ...state }, path, valueToSet);
};

let state = {};
let listeners = [];

function getState(path = []) {
  return getIn(state, path, state);
}

function setState(mutations) {
  if (!Array.isArray(mutations)) {
    mutations = [mutations];
  }

  if (mutations.length === 0) {
    throw new Error('No mutations');
  }

  mutations.forEach(function applyMutation(mutation) {
    if (!mutation) {
      throw new Error('No mutation');
    }

    const { path = [], newValue } = mutation;

    if (!path.length) {
      throw new Error('Empty path in mutation');
    }

    state = setIn(state, path, newValue);
  });

  listeners.forEach(function execute(currentListener) {
    currentListener(state);
  });
}

function reset (newState) {
  state = newState;

  listeners.forEach(function execute(currentListener) {
    currentListener(state);
  });
}

function subscribeListener(listener) {
  listeners.push(listener);
}

function unsubscribeListener(listener) {
  listeners = listeners.filter(currentListener => currentListener !== listener);
}

function initialize(initialState = {}) {
  state = initialState;
}

function unsubscribeAllListeners() {
  listeners = [];
}

var store = {
  getState,
  setState,
  initialize,
  reset
};

var storeModule = /*#__PURE__*/Object.freeze({
__proto__: null,
getState: getState,
setState: setState,
reset: reset,
subscribeListener: subscribeListener,
unsubscribeListener: unsubscribeListener,
initialize: initialize,
unsubscribeAllListeners: unsubscribeAllListeners,
'default': store
});

function useForceUpdate() {
  const [, dispatch] = useState(Object.create(null));

  const memoizedDispatch = useCallback(() => {
    dispatch(Object.create(null));
  }, [dispatch]);
  return memoizedDispatch;
}

function subscribe(
  ComponentToSubscribe,
  mapStateToProps,
  injectedProps = {},
  store = storeModule
) {
  function subscribeChild(Child) {
    function Subscriber(props) {
      const forceUpdate = useForceUpdate();
      let currentMappedProps = mapStateToProps(store.getState(), props);

      function update() {
        const nextMappedProps = mapStateToProps(store.getState(), props);

        if (!isEqual(nextMappedProps, currentMappedProps)) {
          currentMappedProps = nextMappedProps;
          forceUpdate();
        }
      }

      // Will receive props
      useEffect(() => {
        update();
      }, [props]);

      useEffect(() => {
        // Did mount
        store.subscribeListener(update);

        // Will unmount
        return () => store.unsubscribeListener(update);
      }, []);

      return createElement(Child, {
        ...props,
        ...currentMappedProps,
        ...injectedProps,
      });
    }

    return memo(Subscriber);
  }

  return subscribeChild(ComponentToSubscribe);
}

function useSelector(selector) {
  const forceUpdate = useForceUpdate();
  let currentSelectedState = selector(getState());

  function update() {
    const nextSelectedState = selector(getState());

    if (isEqual(nextSelectedState, currentSelectedState)) {
      return;
    }

    currentSelectedState = nextSelectedState;
    forceUpdate();
  }

  useEffect(() => {
    // Did mount
    subscribeListener(update);

    // Will unmount
    return () => unsubscribeListener(update);
  }, []);

  return currentSelectedState;
}

function stateGet(extractions) {
    return coeffect('state', extractions);
}

function registerStateCoeffect(store) {
  registerCoeffectHandler('state', function state(extractions) {
    const result = Object.entries(extractions).reduce(
      function extractStateParts(acc, [key, path]) {
        acc[key] = store.getState(path);
        return acc;
      },
      {}
    );
    return { state: result };
  });
}

function stateSet(mutations) {
  return {
    'state.set': mutations,
  };
}

function registerSetStateEffect(store) {
  registerEffectHandler('state.set', function setStateEffect(mutations) {
    // eslint-disable-next-line no-param-reassign
    mutations = Object.entries(mutations).map(function([path, newValue]) {
      return { path, newValue };
    });
    store.setState(mutations);
  });
}

const MUTATE_STATE_EFFECT_ID = 'state.mutate';

function mutateState(fn, ...args) {
  return {
    [MUTATE_STATE_EFFECT_ID]: { fn, args },
  };
}

function registerMutateStateEffect(store) {
  registerEffectHandler(MUTATE_STATE_EFFECT_ID, function mutateStateEffect({ fn, args }) {
    const state = store.getState();
    const newState = fn(state, ...args);
    store.reset(newState);
  });
}

const MERGE_STATE_EFFECT_ID = 'state.merge';

function registerMergeStateEffect(store) {
  registerEffectHandler(MERGE_STATE_EFFECT_ID, function mergeStateEffect(
    deepPartialState
  ) {
    const state = store.getState();
    store.reset(
      deepmerge(state, deepPartialState, {
        arrayMerge: (_, sourceArray) => sourceArray,
        isMergeableObject: isPlainObject,
      })
    );
  });
}

function stateMerge(deepPartialState) {
  return {
    [MERGE_STATE_EFFECT_ID]: deepPartialState,
  };
}

const state$1 = {
  get: stateGet,
  set: stateSet,
  merge: stateMerge,
  mutate: mutateState,
};

function registerStateBatteries(store$1 = store) {
  registerStateCoeffect(store$1);
  registerSetStateEffect(store$1);
  registerMergeStateEffect(store$1);
  registerMutateStateEffect(store$1);
}

const devToolsOn =
  process.env.NODE_ENV === 'development' && typeof window !== 'undefined';

if (devToolsOn) {
  window['__REFFECTS_DEV_TOOLS__'] = {
    ...window['__REFFECTS_DEV_TOOLS__'],
    getState: store.getState,
    setState: store.setState,
    initialize: store.initialize,
    reset: store.reset,
  };
}

export { registerStateBatteries, state$1 as state, store, subscribe, useSelector };
